// Single source of truth for the initial `params` state.
//
// UI panes read from `params` and dispatch intents to change values — they
// must NOT seed defaults themselves. Add new keys here (and to
// src/config/defaults.js if the value is also consumed by scene/physics
// modules). Runtime-dependent values (themeName depends on the loaded theme
// list) stay as one-shot guards at their pane builders.

// Sibling .d.ts in src/config/ supplies the types while defaults.js is still JS.
import {
  SLICE_DEFAULTS,
  CONTOUR_DEFAULTS,
  FIELDLINE_DEFAULTS,
  SCENE_DEFAULTS,
  DATASET_DEFAULTS,
} from '../config/defaults';
import type { ParamsState } from './schema';

export function buildInitialParams(): ParamsState {
  return {
    // dataset & field selection
    dataset: DATASET_DEFAULTS.key,
    fieldComponent: DATASET_DEFAULTS.component,
    activeFields: [DATASET_DEFAULTS.component],

    // timestep scrubber (inert until a reader exposes >1 step)
    currentStep: 0,
    availableSteps: [0],

    // colorbar units toggle (stub; pairs with SET_COLORBAR_UNITS)
    colorbarUnits: 'native',

    // camera projection (overridden at boot by the persisted localStorage value)
    cameraProjection: 'perspective',

    // scene navigation mode (overridden at boot by the persisted localStorage value)
    navMode: 'orbit',

    // colormap (legacy globals — phased out as bindings take over)
    colormap: 'Plasma',
    colormapMin: 0,
    colormapMax: 1,
    centerOnZero: false,
    colormapUnits: '',
    transparentBounds: false,
    colormapLog: false,
    statsMedian1Std: '',
    statsRange: '',

    // colormap bindings (Stage 2) — populated by applyComponentColormapDefaults
    // on dataset load. Empty at boot.
    bindings: {},
    bindingOrder: [],
    nextBindingSeq: 1,
    slicesColormapId: null,
    volumeColormapId: null,
    contourColormapId: null,
    fieldlinesColormapId: null,
    visibleColorbarIds: [],

    // per-primitive Domain. 'full' = whole dataset; selection ids point at
    // entries in `selections` below.
    slicesDomain: 'full',
    contourDomain: 'full',
    volumeDomain: 'full',
    fieldLinesDomain: 'full',
    reductionsDomain: 'full',

    // selections registry (Stage 5). Empty at boot — selections are
    // session-only in Stage 5 (no OPFS persistence); future work can hydrate.
    selections: {},
    selectionOrder: [],
    nextSelectionSeq: 1,
    focusedSelectionId: null,

    // reductions registry (Stage 6). Session-only.
    reductions: {},
    reductionOrder: [],
    nextReductionSeq: 1,
    focusedReductionId: null,

    // slice visibility + position
    showFieldSlice: true,
    showFieldSliceZY: true,
    showFieldSliceZX: true,
    sliceZ: 0,
    sliceZY: 0,
    sliceZX: 0,

    // slice appearance
    sliceOpacity: 1.0,
    sliceResolution: SLICE_DEFAULTS.resolution,
    slicePixelated: !!SLICE_DEFAULTS.pixelated,
    sliceMaterial: SLICE_DEFAULTS.material,
    sliceDepthWrite: !!SLICE_DEFAULTS.depthWrite,
    sliceDepthTest: !!SLICE_DEFAULTS.depthTest,
    sliceDoubleSide: !!SLICE_DEFAULTS.doubleSide,
    sliceAlphaTest: SLICE_DEFAULTS.alphaTest,
    sliceMetalness: SLICE_DEFAULTS.metalness,
    sliceRoughness: SLICE_DEFAULTS.roughness,
    sliceRenderOrder: SLICE_DEFAULTS.renderOrder,
    sliceCastShadow: !!SLICE_DEFAULTS.castShadow,
    sliceReceiveShadow: !!SLICE_DEFAULTS.receiveShadow,

    // grids
    showGridXY: true,
    showGridXZ: false,
    showGridYZ: false,

    // field lines
    showFieldLines: true,
    fieldLineStep: 0.1,
    fieldLineMaxSteps: 5000,
    fieldLineTubes: !!FIELDLINE_DEFAULTS.tubes,
    fieldLineOpacity: FIELDLINE_DEFAULTS.opacity,
    fieldLineRadius: FIELDLINE_DEFAULTS.radius,
    fieldLineMaterial: FIELDLINE_DEFAULTS.material,
    fieldLineDepthWrite: !!FIELDLINE_DEFAULTS.depthWrite,
    fieldLineDepthTest: !!FIELDLINE_DEFAULTS.depthTest,
    fieldLineRenderOrder: FIELDLINE_DEFAULTS.renderOrder,
    fieldLineCastShadow: !!FIELDLINE_DEFAULTS.castShadow,
    fieldLineReceiveShadow: !!FIELDLINE_DEFAULTS.receiveShadow,

    // volume rendering
    volumeEnabled: false,
    volumeSteps: 64,
    volumeDensity: 8.0,
    volumeBrightness: 1.0,

    // contour
    showContours: true,
    contourValue: 0.0,
    contourOpacity: CONTOUR_DEFAULTS.opacity,
    contourMetalness: CONTOUR_DEFAULTS.metalness,
    contourRoughness: CONTOUR_DEFAULTS.roughness,
    contourEnvIntensity: CONTOUR_DEFAULTS.envIntensity,
    contourDoubleSide: !!CONTOUR_DEFAULTS.doubleSide,
    contourDepthWrite: !!CONTOUR_DEFAULTS.depthWrite,
    contourRenderOrder: CONTOUR_DEFAULTS.renderOrder,

    // selection overlay
    showSelectionCube: false,
    selectionCubeSize: SCENE_DEFAULTS.selectionSize,
    showSelection: true,
    showAxesVectors: true,

    position: { x: 0, y: 0, z: 0 },
  };
}
